import { useMemo } from "react";
import { Marker } from "react-leaflet";
import L from "leaflet";
import { useNavigate } from "@tanstack/react-router";
import { Map } from "@/components/ui/map";
import { SupplierOfficeListItem } from "@/app/Offices/GraphQL/supplierOfficeListQuery";
import { BasemapLayer } from "@/components/map/BasemapLayer";
import { FitBounds } from "@/components/map/FitBounds";

const AUSTRALIA_CENTER: [number, number] = [-25.2744, 133.7751];

const officeIcon = L.divIcon({
  html: `
    <div style="display: flex; flex-direction: column; align-items: center;">
      <div style="background-color: #4f46e5; width: 22px; height: 22px; border-radius: 50% 50% 50% 0; transform: rotate(-45deg); border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>
    </div>
  `,
  className: "",
  iconSize: [22, 22],
  iconAnchor: [11, 22],
  popupAnchor: [0, -22],
});

function isValidCoord(lat?: number | null, lng?: number | null): boolean {
  return (
    typeof lat === "number" &&
    typeof lng === "number" &&
    isFinite(lat) &&
    isFinite(lng)
  );
}

export function OfficeMap({
  offices,
  height = "500px",
}: {
  offices: SupplierOfficeListItem[];
  height?: string;
}) {
  const navigate = useNavigate();

  const points = useMemo(
    () =>
      offices
        .filter((office) =>
          isValidCoord(office.address.lat, office.address.lng),
        )
        .map((office) => ({
          office,
          position: [office.address.lat!, office.address.lng!] as [
            number,
            number,
          ],
        })),
    [offices],
  );

  const bounds: L.LatLngBoundsExpression | null =
    points.length >= 2
      ? (points.map((p) => p.position) as L.LatLngBoundsExpression)
      : null;

  const center = points.length === 1 ? points[0].position : AUSTRALIA_CENTER;

  function openOffice(officeId: string) {
    return navigate({
      to: ".",
      search: (prev: any) => ({ ...prev, officeId }),
    });
  }

  return (
    <Map
      center={center}
      zoom={points.length === 1 ? 12 : 4}
      scrollWheelZoom={false}
      height={height}
      className="z-0 overflow-hidden rounded-md"
    >
      <FitBounds bounds={bounds} maxZoom={13} />
      <BasemapLayer />

      {points.map(({ office, position }) => (
        <Marker
          key={office.id}
          position={position}
          icon={officeIcon}
          eventHandlers={{ click: () => openOffice(office.id) }}
        />
      ))}
    </Map>
  );
}
